import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Title, Group, Button, Drawer, TextInput, Textarea, Select, MultiSelect, NumberInput, Stack, Modal, Text, SegmentedControl, Badge, ActionIcon, Autocomplete, Table, Tooltip, Popover, Checkbox } from '@mantine/core';
import { useForm } from '@mantine/form';
import { toast } from '../ui/toast';
import { confirm } from '../ui/confirm';
import FullCalendar from '@fullcalendar/react';
import frLocale from '@fullcalendar/core/locales/fr';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { IconPlus, IconPencil, IconTrash, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import PlanningHeader from '../components/planning/PlanningHeader';
import PlanningSettingsModal from '../components/planning/PlanningSettingsModal';
import ShareCalendarModal from '../components/modals/ShareCalendarModal';
import ManageMembersModal from '../components/modals/ManageMembersModal';
import useMembersManager from '../hooks/useMembersManager';
import PlanningSidebar from '../components/planning/PlanningSidebar';
import PlanningExportMenu from '../components/planning/PlanningExportMenu';
// Replaced separate requests modal with a combined ManageMembersModal
import { auth, db } from '../firebase';
import { collection, addDoc, doc, getDoc, onSnapshot, query, orderBy, Timestamp, updateDoc, deleteDoc, where, getDocs } from 'firebase/firestore';
import { PlanService } from '../services/billing/PlanService';
import { OptimizationService } from '../services/optimization/OptimizationService';
import AvailabilityModal from '../components/AvailabilityModal';
import AdminEventAvailabilities from '../components/availability/AdminEventAvailabilities';
import { useAvailabilities } from '../hooks/useAvailabilities';
import { useMediaQuery } from '@mantine/hooks';
import MobileDayNavigator from '../components/planning/MobileDayNavigator';
import '../components/planning/calendar/fullcalendar-skin.css';
import { diagnoseWhyNotAssignedForEvent } from '../services/diagnostics/assignmentDiagnostics';

// FullCalendar styles (match v5 CSS paths)
//import '@fullcalendar/core/main.css';
//import '@fullcalendar/daygrid/main.css';
//import '@fullcalendar/timegrid/main.css';

// Options built with i18n inside component

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addWeeks(date, weeks) {
  return addDays(date, weeks * 7);
}

function addMonthsSafe(date, months) {
  const d = new Date(date);
  const targetMonth = d.getMonth() + months;
  const targetYear = d.getFullYear() + Math.floor(targetMonth / 12);
  const month = (targetMonth % 12 + 12) % 12;
  const day = d.getDate();
  // Set to first of month then set date to clamp to last day of month if needed
  const tmp = new Date(targetYear, month, 1, d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
  const lastDay = new Date(targetYear, month + 1, 0).getDate();
  tmp.setDate(Math.min(day, lastDay));
  return tmp;
}

//

function formatDateTimeLocalRounded30(date = new Date()) {
  const d = new Date(date);
  // Round down to nearest 30 minutes
  const minutes = d.getMinutes();
  d.setMinutes(minutes - (minutes % 30), 0, 0);
  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function minutesToHHMM(mins) {
  const m = Math.max(0, Number(mins) || 0);
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  const hh = String(h).padStart(2, '0');
  return `${hh}:${mm}`;
}

function hhmmToMinutes(str) {
  if (!str) return NaN;
  const m = String(str).trim();
  const match = m.match(/^(\d{1,2})[:hH]?(\d{2})?$/);
  if (!match) return NaN;
  const h = Number(match[1] || 0);
  const mm = Number(match[2] != null ? match[2] : 0);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return NaN;
  return h * 60 + mm;
}

function roundToNearest30(mins) {
  const m = Number(mins) || 0;
  return Math.round(m / 30) * 30;
}

function buildDurationOptions() {
  const opts = [];
  // Up to 12 hours (720 minutes), in 30-minute steps
  for (let m = 30; m <= 720; m += 30) {
    opts.push({ value: String(m), label: minutesToHHMM(m) });
  }
  return opts;
}

export default function Planning() {
  const { t, i18n } = useTranslation('common');
  const isFr = i18n.language && i18n.language.startsWith('fr');
  const exportLocale = isFr ? 'fr-FR' : 'en-US';
  const weekdayOptions = useMemo(() => ([
    { value: '0', label: t('planning.weekdays.0') },
    { value: '1', label: t('planning.weekdays.1') },
    { value: '2', label: t('planning.weekdays.2') },
    { value: '3', label: t('planning.weekdays.3') },
    { value: '4', label: t('planning.weekdays.4') },
    { value: '5', label: t('planning.weekdays.5') },
    { value: '6', label: t('planning.weekdays.6') },
  ]), [t]);

  const freqOptions = useMemo(() => ([
    { value: 'none', label: t('planning.freq.none') },
    { value: 'daily', label: t('planning.freq.daily') },
    { value: 'weekly_1', label: t('planning.freq.weekly_1') },
    { value: 'weekly_2', label: t('planning.freq.weekly_2') },
    { value: 'weekly_3', label: t('planning.freq.weekly_3') },
    { value: 'monthly_1', label: t('planning.freq.monthly_1') },
    { value: 'monthly_2', label: t('planning.freq.monthly_2') },
  ]), [t]);
  const { id } = useParams();
  const navigate = useNavigate();

  const [ownerId, setOwnerId] = useState(null); // legacy primary owner
  const [owners, setOwners] = useState([]); // multi-owner support
  const [planningName, setPlanningName] = useState('');
  // const [participants, setParticipants] = useState([]); // uids (unused)
  const [assignableUsers, setAssignableUsers] = useState([]); // [{value,label}]
  const [events, setEvents] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [eventDrawerOpen, setEventDrawerOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editScope, setEditScope] = useState('single'); // 'single' | 'series'
  const [saving, setSaving] = useState(false);
  const [isEditingEvent, setIsEditingEvent] = useState(false);
  // Recurrence edit controls (optional)
  const [changeRecurrence, setChangeRecurrence] = useState(false);
  const [seriesEditFrequency, setSeriesEditFrequency] = useState('');
  const [seriesEditWeekdays, setSeriesEditWeekdays] = useState([]); // strings of 0..6
  const [seriesEditUntilDate, setSeriesEditUntilDate] = useState('');
  const [availModalOpen, setAvailModalOpen] = useState(false);
  // requests are provided live by useMembersManager

  const user = auth.currentUser;

  // Map uid -> display label for quick lookups in event rendering
  const { isOwner, owners: ownersHook, participants: membersLive, requests: requestsLive, approveRequest, rejectRequest, removeParticipant, roleSuggestions, addRole, removeRole, addGhostMember, atMemberCap, setContract, setWeeklyHoursMax, makeOwner, removeOwner } = useMembersManager({ db, planningId: id, currentUserId: user?.uid });

  
  const userLabelById = useMemo(() => {
    const m = new Map();
    for (const u of assignableUsers || []) m.set(u.value, u.label);
    for (const p of membersLive || []) m.set(p.uid, p.displayName || p.email || p.uid);
    return m;
  }, [assignableUsers, membersLive]);


  // Helper: update a single event's assignedTo locally to avoid waiting for snapshot
  const updateEventAssignmentsLocal = (eventId, newAssigned) => {
    // newAssigned can be: array of uids, or object { roleId: [uids] }
    const flatArray = Array.isArray(newAssigned)
      ? newAssigned
      : (newAssigned && typeof newAssigned === 'object'
          ? Array.from(new Set(Object.values(newAssigned).flat().filter(Boolean)))
          : []);
    const assignmentArray = Array.isArray(newAssigned)
      ? newAssigned.map((uid) => ({ role: 'Member', uid }))
      : (newAssigned && typeof newAssigned === 'object'
          ? Object.entries(newAssigned).flatMap(([role, uids]) => (Array.isArray(uids) ? uids.map((uid) => ({ role, uid })) : []))
          : []);
    setEvents((prev) => prev.map((ev) => (
      ev.id === eventId
        ? { ...ev, extendedProps: { ...ev.extendedProps, assignedTo: flatArray, assignments: assignmentArray } }
      : ev
    )));
    // Keep selectedEvent in sync if it is the same event
    setSelectedEvent((prev) => prev && prev.id === eventId ? { ...prev, assignedTo: flatArray, assignments: assignmentArray } : prev);
  };

  useEffect(() => {
    if (!id) return;
    // Load planning to determine owner
    (async () => {
      try {
        const planningRef = doc(db, 'plannings', id);
        const snap = await getDoc(planningRef);
        if (snap.exists()) {
          const data = snap.data();
          setOwnerId(data.owner || null);
          const ownersArr = Array.isArray(data.owners) && data.owners.length ? Array.from(new Set(data.owners.filter(Boolean))) : (data.owner ? [data.owner] : []);
          setOwners(ownersArr);
          setPlanningName(data.name || t('planning.default_name'));
          setOptimizationProfile(data.optimizationProfile || 'default');
          const loadedMediaMax = Number(data.optimizationMediaMaxContinuousHours);
          const loadedMediaBreak = Number(data.optimizationMediaMinBreakMinutes);
          const loadedDailyMax = Number(data.optimizationMediaDailyMaxHours);
          const loadedMinDailyRest = Number(data.optimizationMediaMinDailyRestHours);
          const loadedMaxConsec = Number(data.optimizationMediaMaxConsecutiveDays);
          const loadedAmplitude = Number(data.optimizationMediaAmplitudeMaxHours);
          const loadedMinDaysOff = Number(data.optimizationMediaMinWeeklyDaysOff);
          const loadedLocMinBreak = Number(data.optimizationLocationMinBreakMinutes);
          const loadedSeriesConsistency = !!data.optimizationMediaSeriesConsistency;
          const loadedSeriesConsistencyWeight = Number(data.optimizationMediaSeriesConsistencyWeight);
          const loadedWeeklyDefaultInternal = Number(data.optimizationWeeklyDefaultInternal);
          const loadedWeeklyDefaultExternal = Number(data.optimizationWeeklyDefaultExternal);
          setOptimizationOverrides({
            mediaMaxContinuousHours: (Number.isFinite(loadedMediaMax) && loadedMediaMax > 0) ? loadedMediaMax : 6,
            mediaMinBreakMinutes: (Number.isFinite(loadedMediaBreak) && loadedMediaBreak > 0) ? loadedMediaBreak : 20,
            mediaDailyMaxHours: (Number.isFinite(loadedDailyMax) && loadedDailyMax > 0) ? loadedDailyMax : 10,
            mediaMinDailyRestHours: (Number.isFinite(loadedMinDailyRest) && loadedMinDailyRest > 0) ? loadedMinDailyRest : 11,
            mediaAmplitudeMaxHours: (Number.isFinite(loadedAmplitude) && loadedAmplitude > 0) ? loadedAmplitude : 11,
            mediaMaxConsecutiveDays: (Number.isFinite(loadedMaxConsec) && loadedMaxConsec > 0) ? loadedMaxConsec : 6,
            mediaMinWeeklyDaysOff: (Number.isFinite(loadedMinDaysOff) && loadedMinDaysOff >= 0) ? loadedMinDaysOff : 2,
            locationMinBreakMinutes: (Number.isFinite(loadedLocMinBreak) && loadedLocMinBreak > 0) ? loadedLocMinBreak : 30,
            mediaSeriesConsistencyEnabled: loadedSeriesConsistency === true,
            mediaSeriesConsistencyPenalty: (Number.isFinite(loadedSeriesConsistencyWeight) && loadedSeriesConsistencyWeight >= 0) ? loadedSeriesConsistencyWeight : 0.4,
            weeklyDefaultInternal: (Number.isFinite(loadedWeeklyDefaultInternal) && loadedWeeklyDefaultInternal > 0) ? loadedWeeklyDefaultInternal : 40,
            weeklyDefaultExternal: (Number.isFinite(loadedWeeklyDefaultExternal) && loadedWeeklyDefaultExternal > 0) ? loadedWeeklyDefaultExternal : 44,
          });
          // Assignable users now come from live members hook; keep legacy fallback if hook not ready
          if (!assignableUsers.length) {
            const uids = [...(Array.isArray(data.owners) && data.owners.length ? data.owners : (data.owner ? [data.owner] : [])), ...(data.participants || [])].filter(Boolean);
            const userDocs = await Promise.all(
              uids.map((uid) => getDoc(doc(db, 'users', uid)).catch(() => null))
            );
            const options = userDocs
              .filter((s) => s && s.exists())
              .map((s) => {
                const d = s.data();
                return { value: s.id, label: d.displayName || d.email || s.id };
              });
            setAssignableUsers(options);
          }
        }
      } catch (e) {
        console.error('Failed to load planning', e);
      }
    })();

    // Subscribe to events
    const eventsRef = collection(db, 'plannings', id, 'events');
    const q = query(eventsRef, orderBy('start'));
      const unsub = onSnapshot(q, (snapshot) => {
        const evts = snapshot.docs.map((d) => {
          const data = d.data();
          const start = data.start?.toDate ? data.start.toDate() : new Date(data.start);
          const end = data.end?.toDate ? data.end.toDate() : new Date(data.end);
          const rolesRequired = Array.isArray(data.rolesRequired)
            ? data.rolesRequired.map((r) => ({ role: String(r.role || 'Member'), count: Math.max(1, Number(r.count) || 1) }))
            : [{ role: 'Member', count: Math.max(1, Number(data.staffRequired) || 1) }];
          const assignments = Array.isArray(data.assignments)
            ? data.assignments.filter((a) => a && a.role && a.uid)
            : (data.assignments && typeof data.assignments === 'object'
                ? Object.entries(data.assignments).flatMap(([role, uids]) => (Array.isArray(uids) ? uids.map((uid) => ({ role, uid })) : []))
                : (Array.isArray(data.assignedTo)
                    ? data.assignedTo.map((uid) => ({ role: 'Member', uid }))
                    : (data.assignedTo ? [{ role: 'Member', uid: data.assignedTo }] : [])
                  )
              );
          return {
            id: d.id,
            title: data.title || t('planning.default_event_title'),
            start,
            end,
          extendedProps: {
              description: data.description || '',
              location: data.location || '',
              effectiveMinutes: (Number(data.effectiveMinutes) || 0) > 0 ? Number(data.effectiveMinutes) : null,
              rolesRequired,
              assignments,
              seriesId: data.seriesId || null,
              linkedEventIds: Array.isArray(data.linkedEventIds) ? Array.from(new Set(data.linkedEventIds.filter((x) => typeof x === 'string' && x))) : [],
              // legacy fields (for components not yet migrated)
              staffRequired: Number(data.staffRequired) || rolesRequired.reduce((s, r) => s + (Number(r.count) || 0), 0),
              assignedTo: Array.from(new Set(assignments.map((a) => a.uid))),
            },
          };
        });
        setEvents(evts);
      });
    return () => unsub();
  }, [id, assignableUsers.length, t]);

  // Keep assignable users list in sync with membersLive (users + ghosts)
  useEffect(() => {
    const opts = Array.isArray(membersLive) ? membersLive.map((p) => ({ value: p.uid, label: p.displayName || p.email || p.uid })) : [];
    if (opts.length) setAssignableUsers(opts);
  }, [membersLive]);

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      location: '',
      startAt: formatDateTimeLocalRounded30(), // datetime-local string default to today
      untilDate: '', // date string (inclusive)
      durationMinutes: 60,
      effectiveMinutes: 0,
      rolesRequired: [{ role: 'Member', count: 1 }],
      frequency: 'none',
      weekdays: [], // strings of 0..6
    },
    validate: {
      title: (v) => (v?.trim().length ? null : 'Title is required'),
      startAt: (v) => (v ? null : 'Start date/time is required'),
      durationMinutes: (v) => (v >= 30 ? null : 'Duration must be at least 30 minutes'),
      rolesRequired: (rows) => {
        if (!Array.isArray(rows) || rows.length === 0) return 'At least one role is required';
        for (const r of rows) {
          if (!r || !String(r.role || '').trim()) return 'Role name is required';
          if (!Number.isFinite(Number(r.count)) || Number(r.count) < 1) return 'Count must be >= 1';
        }
        return null;
      },
      frequency: (v) => (v ? null : 'Choose a frequency'),
      weekdays: (value, values) => {
        if (values.frequency === 'weekly_1') return value.length > 0 ? null : 'Choose at least one day';
        if (values.frequency === 'weekly_2' || values.frequency === 'weekly_3') return value.length > 0 ? null : 'Choose at least one day';
        return null;
      },
      untilDate: (v, values) => {
        if (values.frequency === 'none') return null; // not required
        return v ? null : 'End date is required for repeating events';
      },
    },
  });

  const canCreate = !!(user && ((Array.isArray(owners) && owners.includes(user.uid)) || (ownerId && user.uid === ownerId)));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [peopleOpen, setPeopleOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [optimizationProfile, setOptimizationProfile] = useState('default');
  const [optimizationOverrides, setOptimizationOverrides] = useState({
    mediaMaxContinuousHours: 6,
    mediaMinBreakMinutes: 20,
    mediaDailyMaxHours: 10,
    mediaMinDailyRestHours: 11,
    mediaAmplitudeMaxHours: 11,
    mediaMaxConsecutiveDays: 6,
    mediaMinWeeklyDaysOff: 2,
    weeklyDefaultInternal: 40,
    weeklyDefaultExternal: 44,
  });
  const [filterUserId, setFilterUserId] = useState(null);
  const [filterText, setFilterText] = useState('');
  const filterUserOptions = useMemo(() => {
    // Merge assignableUsers with live members to ensure ghosts are included
    const map = new Map();
    (assignableUsers || []).forEach((u) => map.set(u.value, u.label));
    (membersLive || []).forEach((m) => map.set(m.uid, m.displayName || m.email || m.uid));
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [assignableUsers, membersLive]);
  const filteredEvents = useMemo(() => {
    const text = String(filterText || '').trim().toLowerCase();
    const byUser = String(filterUserId || '').trim();
    if (!Array.isArray(events) || !events.length) return [];
    return events.filter((ev) => {
      if (text) {
        const title = String(ev?.title || '').toLowerCase();
        const desc = String(ev?.extendedProps?.description || '').toLowerCase();
        if (!(title.includes(text) || desc.includes(text))) return false;
      }
      if (byUser) {
        const assigns = Array.isArray(ev?.extendedProps?.assignments)
          ? ev.extendedProps.assignments
          : (ev?.extendedProps?.assignedTo ? [{ uid: ev.extendedProps.assignedTo, role: 'Member' }] : []);
        const uids = Array.isArray(assigns)
          ? assigns.map((a) => a && (a.uid || a.value)).filter(Boolean)
          : [];
        if (!uids.includes(byUser)) return false;
      }
      return true;
    });
  }, [events, filterText, filterUserId]);
  const calendarRef = useRef(null);
  const [clearing, setClearing] = useState(false);

  // Availability declarations for current user to display count badge
  const { map: availMap } = useAvailabilities(id, user?.uid);
  const undeclaredCount = useMemo(() => {
    if (!events?.length) return 0;
    let c = 0;
    for (const ev of events) {
      if (!availMap.has(ev.id)) c += 1;
    }
    return c;
  }, [events, availMap]);

  const handleCreate = async (vals) => {
    setSaving(true);
    try {
      const startBase = new Date(vals.startAt);
      if (Number.isNaN(startBase.getTime())) throw new Error('Invalid start date/time');
      const until = vals.untilDate ? new Date(vals.untilDate + 'T23:59:59') : null; // inclusive end of day
      const durationMs = (Number(vals.durationMinutes) || 0) * 60 * 1000;

      const occurrences = [];

      const pushOcc = (dt) => {
        const end = new Date(dt.getTime() + durationMs);
        occurrences.push({ start: new Date(dt), end });
      };

      const freq = vals.frequency;

      if (freq === 'none') {
        pushOcc(startBase);
      } else if (freq === 'daily') {
        if (!until) throw new Error('End date required for daily');
        let d = new Date(startBase);
        while (d <= until) {
          pushOcc(d);
          d = addDays(d, 1);
        }
      } else if (freq.startsWith('weekly_')) {
        if (!until) throw new Error('End date required for weekly');
        const interval = Number(freq.split('_')[1]);
        const hours = startBase.getHours();
        const minutes = startBase.getMinutes();
        const seconds = startBase.getSeconds();
        const ms = startBase.getMilliseconds();

        if (interval === 1) {
          const targetWeekdays = new Set((vals.weekdays || []).map((s) => Number(s)));
          let d = new Date(startBase);
          while (d <= until) {
            if (targetWeekdays.has(d.getDay())) {
              const dt = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, seconds, ms);
              pushOcc(dt);
            }
            d = addDays(d, 1);
          }
        } else {
          // Multiple weekdays allowed; build a stream for each selected weekday
          const targetWeekdays = (vals.weekdays && vals.weekdays.length)
            ? Array.from(new Set(vals.weekdays.map((s) => Number(s))))
            : [startBase.getDay()];
          for (const weekday of targetWeekdays) {
            let first = new Date(startBase);
            const diff = (weekday - first.getDay() + 7) % 7;
            first = addDays(first, diff);
            while (first <= until) {
              const dt = new Date(first.getFullYear(), first.getMonth(), first.getDate(), hours, minutes, seconds, ms);
              pushOcc(dt);
              first = addWeeks(first, interval);
            }
          }
        }
      } else if (freq.startsWith('monthly_')) {
        if (!until) throw new Error('End date required for monthly');
        const interval = Number(freq.split('_')[1]);
        const hours = startBase.getHours();
        const minutes = startBase.getMinutes();
        const seconds = startBase.getSeconds();
        const ms = startBase.getMilliseconds();
        const selectedWds = Array.isArray(vals.weekdays) ? vals.weekdays.map((s) => Number(s)) : [];
        let cursor = new Date(startBase);
        while (cursor <= until) {
          if (selectedWds.length) {
            // For this month, add all days matching selected weekdays
            const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1, hours, minutes, seconds, ms);
            const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, hours, minutes, seconds, ms);
            let d = new Date(monthStart);
            while (d <= monthEnd && d <= until) {
              if (selectedWds.includes(d.getDay())) pushOcc(d);
              d = addDays(d, 1);
            }
          } else {
            // Fallback: same day-of-month recurrence
            pushOcc(new Date(cursor));
          }
          cursor = addMonthsSafe(cursor, interval);
        }
      }

      // Write to Firestore under plannings/{id}/events
      const eventsRef = collection(db, 'plannings', id, 'events');
      // seriesId to link repeating events
      const seriesId = vals.frequency !== 'none' ? doc(eventsRef).id : null;
      for (const occ of occurrences) {
        const rolesRequired = Array.isArray(vals.rolesRequired)
          ? vals.rolesRequired.map((r) => ({ role: String(r.role || '').trim(), count: Math.max(1, Number(r.count) || 1) }))
          : [];
        if (rolesRequired.length === 0 || rolesRequired.some((r) => !r.role)) {
          throw new Error('Role name required');
        }
        const staffRequiredSum = rolesRequired.reduce((s, r) => s + (Number(r.count) || 0), 0);
        const eff = Math.max(0, Math.min(Math.round(Number(vals.effectiveMinutes) || 0), Math.round(durationMs/60000)));
        await addDoc(eventsRef, {
          title: vals.title.trim(),
          description: vals.description?.trim() || '',
          location: (vals.location || '').trim() || '',
          start: Timestamp.fromDate(occ.start),
          end: Timestamp.fromDate(occ.end),
          effectiveMinutes: eff > 0 ? eff : null,
          // New per-role requirements and assignments
          rolesRequired,
          assignments: [],
          // Backward compatibility fields
          assignedTo: null,
          staffRequired: staffRequiredSum,
          seriesId: seriesId,
          createdAt: Timestamp.fromDate(new Date()),
          createdBy: user?.uid || null,
        });
      }

      setDrawerOpen(false);
      form.reset();
    } catch (e) {
      console.error('Failed to create events', e);
      toast.show({ title: 'Invalid roles', message: 'Please fill role names before creating the event', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const formatDateTimeLocal = (date) => {
    const d = new Date(date);
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mi = pad(d.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  };

  // isOwner now provided by useMembersManager hook below

  const formatDisplay = (date) => {
    try {
      const d = new Date(date);
      return d.toLocaleString();
    } catch {
      return '';
    }
  };

  const handleEventClick = (info) => {
    const e = info.event;
    const data = {
      id: e.id,
      title: e.title,
      description: e.extendedProps?.description || '',
      location: e.extendedProps?.location || '',
      start: new Date(e.start),
      end: new Date(e.end || e.start),
      // legacy flat assignments for backward compatibility
      assignedTo: Array.isArray(e.extendedProps?.assignedTo)
        ? e.extendedProps.assignedTo
        : (e.extendedProps?.assignedTo ? [e.extendedProps.assignedTo] : []),
      // new per-role fields
      rolesRequired: Array.isArray(e.extendedProps?.rolesRequired) ? e.extendedProps.rolesRequired : [{ role: 'Member', count: Number(e.extendedProps?.staffRequired) || 1 }],
      assignments: Array.isArray(e.extendedProps?.assignments)
        ? e.extendedProps.assignments
        : (e.extendedProps?.assignments && typeof e.extendedProps.assignments === 'object'
            ? Object.entries(e.extendedProps.assignments).flatMap(([role, uids]) => (Array.isArray(uids) ? uids.map((uid) => ({ role, uid })) : []))
            : (Array.isArray(e.extendedProps?.assignedTo) ? e.extendedProps.assignedTo.map((uid) => ({ role: 'Member', uid })) : [])
          ),
      seriesId: e.extendedProps?.seriesId || null,
      effectiveMinutes: (Number(e.extendedProps?.effectiveMinutes) || 0) > 0 ? Number(e.extendedProps?.effectiveMinutes) : null,
      linkedEventIds: Array.isArray(e.extendedProps?.linkedEventIds) ? e.extendedProps.linkedEventIds : [],
      staffRequired: Number(e.extendedProps?.staffRequired) || 1,
    };
    setSelectedEvent(data);
    setEditScope('single');
    setIsEditingEvent(false);
    setChangeRecurrence(false);
    setSeriesEditFrequency('');
    setSeriesEditWeekdays([]);
    setSeriesEditUntilDate('');
    setEventDrawerOpen(true);
  };

  const saveSelectedEvent = async () => {
    if (!selectedEvent) return;
    setSaving(true);
    try {
      if (isOwner && editScope === 'series' && selectedEvent.seriesId) {
        // Validate roles: must have at least one non-empty role name
        const rows = Array.isArray(selectedEvent.rolesRequired) ? selectedEvent.rolesRequired : [];
        const invalid = rows.length === 0 || rows.some((r) => !String(r.role || '').trim());
        if (invalid) {
          toast.show({ title: 'Missing role', message: 'Please enter a role name for each row', color: 'red' });
          setSaving(false);
          return;
        }
        const eventsRef = collection(db, 'plannings', id, 'events');
        if (changeRecurrence && seriesEditFrequency && seriesEditFrequency !== 'none') {
          // Recreate the series with a new recurrence pattern
          if (!seriesEditUntilDate) {
            toast.show({ title: 'End date required', message: 'Please choose an end date for the new recurrence', color: 'red' });
            setSaving(false);
            return;
          }
          const until = new Date(seriesEditUntilDate + 'T23:59:59');
          const startBase = new Date(selectedEvent.start);
          const endBase = new Date(selectedEvent.end);
          const durationMs = Math.max(0, endBase.getTime() - startBase.getTime());
          const hours = startBase.getHours();
          const minutes = startBase.getMinutes();
          const seconds = startBase.getSeconds();
          const ms = startBase.getMilliseconds();
          const occurrences = [];
          const pushOcc = (dt) => { const end = new Date(dt.getTime() + durationMs); occurrences.push({ start: new Date(dt), end }); };
          const freq = seriesEditFrequency;
          if (freq === 'daily') {
            let d = new Date(startBase);
            while (d <= until) { pushOcc(d); d = addDays(d, 1); }
          } else if (freq.startsWith('weekly_')) {
            const interval = Number(freq.split('_')[1]);
            const targetWeekdays = (Array.isArray(seriesEditWeekdays) && seriesEditWeekdays.length)
              ? Array.from(new Set(seriesEditWeekdays.map((s) => Number(s))))
              : [startBase.getDay()];
            for (const weekday of targetWeekdays) {
              let first = new Date(startBase);
              const diff = (weekday - first.getDay() + 7) % 7;
              first = addDays(first, diff);
              while (first <= until) {
                const dt = new Date(first.getFullYear(), first.getMonth(), first.getDate(), hours, minutes, seconds, ms);
                pushOcc(dt);
                first = addWeeks(first, interval);
              }
            }
          } else if (freq.startsWith('monthly_')) {
            const interval = Number(freq.split('_')[1]);
            let cursor = new Date(startBase);
            if (Array.isArray(seriesEditWeekdays) && seriesEditWeekdays.length) {
              const selectedWds = seriesEditWeekdays.map((s) => Number(s));
              while (cursor <= until) {
                const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1, hours, minutes, seconds, ms);
                const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, hours, minutes, seconds, ms);
                let d = new Date(monthStart);
                while (d <= monthEnd && d <= until) { if (selectedWds.includes(d.getDay())) pushOcc(d); d = addDays(d, 1); }
                cursor = addMonthsSafe(cursor, interval);
              }
            } else {
              while (cursor <= until) { pushOcc(new Date(cursor)); cursor = addMonthsSafe(cursor, interval); }
            }
          }
          // Remove existing series and create new occurrences with same seriesId
          const q = query(eventsRef, where('seriesId', '==', selectedEvent.seriesId));
          const snap = await getDocs(q);
          await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
          const rolesRequired = selectedEvent.rolesRequired.map((r) => ({ role: String(r.role || '').trim(), count: Math.max(1, Number(r.count) || 1) }));
          const staffRequiredSum = rolesRequired.reduce((s, r) => s + (Number(r.count) || 0), 0);
          for (const occ of occurrences) {
            const eff = (() => { const durationMin = Math.max(0, Math.round((occ.end.getTime() - occ.start.getTime())/60000)); const val = Math.max(0, Math.min(Math.round(Number(selectedEvent.effectiveMinutes) || 0), durationMin)); return val > 0 ? val : null; })();
            await addDoc(eventsRef, {
              title: selectedEvent.title || 'Event',
              description: selectedEvent.description || '',
              location: (selectedEvent.location || '').trim() || '',
              start: Timestamp.fromDate(occ.start),
              end: Timestamp.fromDate(occ.end),
              effectiveMinutes: eff,
              rolesRequired,
              assignments: [],
              assignedTo: null,
              staffRequired: staffRequiredSum,
              seriesId: selectedEvent.seriesId,
              createdAt: Timestamp.fromDate(new Date()),
              createdBy: user?.uid || null,
            });
          }
          setEventDrawerOpen(false);
        } else {
          // Apply changes to the entire series (no recurrence change): update time-of-day and roles
          const q = query(eventsRef, where('seriesId', '==', selectedEvent.seriesId));
          const snap = await getDocs(q);
          const newStart = new Date(selectedEvent.start);
          const newEnd = new Date(selectedEvent.end);
          const durationMs = Math.max(0, newEnd.getTime() - newStart.getTime());
          const hours = newStart.getHours();
          const minutes = newStart.getMinutes();
          const updates = snap.docs.map(async (d) => {
            const data = d.data();
            const s = data.start?.toDate ? data.start.toDate() : new Date(data.start);
            // Keep the date, update time-of-day
            const ns = new Date(s);
            ns.setHours(hours, minutes, 0, 0);
            const ne = new Date(ns.getTime() + durationMs);
            // For series, update basic fields and rolesRequired consistently
            const rolesRequired = selectedEvent.rolesRequired.map((r) => ({ role: String(r.role || '').trim(), count: Math.max(1, Number(r.count) || 1) }));
            const staffRequiredSum = rolesRequired.reduce((s, r) => s + (Number(r.count) || 0), 0);
            return updateDoc(d.ref, {
              title: selectedEvent.title || 'Event',
              description: selectedEvent.description || '',
              location: (selectedEvent.location || '').trim() || '',
              start: Timestamp.fromDate(ns),
              end: Timestamp.fromDate(ne),
              rolesRequired,
              effectiveMinutes: (() => { const durationMin = Math.max(0, Math.round((ne.getTime() - ns.getTime())/60000)); const eff = Math.max(0, Math.min(Math.round(Number(selectedEvent.effectiveMinutes) || 0), durationMin)); return eff > 0 ? eff : null; })(),
              // legacy aggregate field
              staffRequired: staffRequiredSum,
            });
          });
          await Promise.all(updates);
          setEventDrawerOpen(false);
        }
      } else {
        // Single event update (includes assignment)
        const ref = doc(db, 'plannings', id, 'events', selectedEvent.id);
        const rows = Array.isArray(selectedEvent.rolesRequired) ? selectedEvent.rolesRequired : [];
        const invalid = rows.length === 0 || rows.some((r) => !String(r.role || '').trim());
        if (invalid) {
          toast.show({ title: 'Missing role', message: 'Please enter a role name for each row', color: 'red' });
          setSaving(false);
          return;
        }
        const rolesRequired = rows.map((r) => ({ role: String(r.role || '').trim(), count: Math.max(1, Number(r.count) || 1) }));
        let assignments = Array.isArray(selectedEvent.assignments) ? selectedEvent.assignments.filter((a) => a && a.role && a.uid) : [];
        // Enforce uniqueness: a user can only hold one role
        const seen = new Set();
        assignments = assignments.filter((a) => {
          if (seen.has(a.uid)) return false;
          seen.add(a.uid);
          return true;
        });
        const staffRequiredSum = rolesRequired.reduce((s, r) => s + (Number(r.count) || 0), 0);
        await updateDoc(ref, {
          title: selectedEvent.title || t('planning.default_event_title'),
          description: selectedEvent.description || '',
          location: (selectedEvent.location || '').trim() || '',
          start: Timestamp.fromDate(new Date(selectedEvent.start)),
          end: Timestamp.fromDate(new Date(selectedEvent.end)),
          rolesRequired,
          assignments,
          linkedEventIds: Array.isArray(selectedEvent.linkedEventIds) ? selectedEvent.linkedEventIds.filter(Boolean) : [],
          effectiveMinutes: (Number(selectedEvent.effectiveMinutes) || 0) > 0 ? Math.min(
            Math.round(Number(selectedEvent.effectiveMinutes) || 0),
            Math.max(0, Math.round((new Date(selectedEvent.end).getTime() - new Date(selectedEvent.start).getTime())/60000))
          ) : null,
          // legacy/back-compat fields
          assignedTo: assignments.length ? Array.from(new Set(assignments.map((a) => a.uid))) : [],
          staffRequired: staffRequiredSum,
        });
        setEventDrawerOpen(false);
      }
    } catch (e) {
      console.error('Failed to save event', e);
    } finally {
      setSaving(false);
    }
  };

  const assignToMe = async () => {
    if (!selectedEvent || !user) return;
    setSaving(true);
    try {
      const ref = doc(db, 'plannings', id, 'events', selectedEvent.id);
      const roles = Array.isArray(selectedEvent.rolesRequired) && selectedEvent.rolesRequired.length ? selectedEvent.rolesRequired : [{ role: 'Member', count: 1 }];
      const primaryRole = roles[0];
      const current = Array.isArray(selectedEvent.assignments) ? selectedEvent.assignments : [];
      if (current.some((a) => a.uid === user.uid)) { setSaving(false); return; }
      const currentForRole = current.filter((a) => a.role === primaryRole.role).map((a) => a.uid);
      if (currentForRole.length >= Math.max(1, Number(primaryRole.count) || 1)) { setSaving(false); return; }
      const next = [...current.filter((a) => a.role !== primaryRole.role), ...[...currentForRole, user.uid].map((uid) => ({ role: primaryRole.role, uid }))];
      await updateDoc(ref, {
        assignments: next,
        assignedTo: Array.from(new Set(next.map((a) => a.uid))),
      });
      setSelectedEvent({ ...selectedEvent, assignments: next, assignedTo: Array.from(new Set(next.map((a) => a.uid))) });
    } catch (e) {
      console.error('Failed to assign to me', e);
    } finally {
      setSaving(false);
    }
  };

  const deleteSingle = async () => {
    if (!selectedEvent) return;
    if (!(await confirm({ title: t('planning.delete_event_title'), message: t('planning.delete_event_message'), color: 'red' }))) return;
    try {
      await deleteDoc(doc(db, 'plannings', id, 'events', selectedEvent.id));
      setDeleteModalOpen(false);
      setEventDrawerOpen(false);
    } catch (e) {
      console.error('Failed to delete event', e);
    }
  };

  const unassignMe = async () => {
    if (!selectedEvent || !user) return;
    setSaving(true);
    try {
      const ref = doc(db, 'plannings', id, 'events', selectedEvent.id);
      const current = Array.isArray(selectedEvent.assignments) ? selectedEvent.assignments : [];
      const next = current.filter((a) => a.uid !== user.uid);
      await updateDoc(ref, { assignments: next, assignedTo: Array.from(new Set(next.map((a) => a.uid))) });
      setSelectedEvent({ ...selectedEvent, assignments: next, assignedTo: Array.from(new Set(next.map((a) => a.uid))) });
    } catch (e) {
      console.error('Failed to unassign me', e);
    } finally {
      setSaving(false);
    }
  };

  // Note: owner unassign helpers removed; inline edit handles it now.

  const deleteSeries = async () => {
    if (!selectedEvent?.seriesId) return;
    if (!(await confirm({ title: t('planning.delete_series_title'), message: t('planning.delete_series_message'), color: 'red' }))) return;
    try {
      const eventsRef = collection(db, 'plannings', id, 'events');
      const q = query(eventsRef, where('seriesId', '==', selectedEvent.seriesId));
      const snap = await getDocs(q);
      const batchDeletes = snap.docs.map((d) => deleteDoc(d.ref));
      await Promise.all(batchDeletes);
      setDeleteModalOpen(false);
      setEventDrawerOpen(false);
    } catch (e) {
      console.error('Failed to delete series', e);
    }
  };

  const weekdaysControl = useMemo(() => {
    const f = form.values.frequency;
    const showMulti = f === 'weekly_1' || f === 'weekly_2' || f === 'weekly_3' || f?.startsWith('monthly_');
    if (!showMulti) return null;
    return (
      <MultiSelect
        label={t('planning.fields.days_of_week')}
        data={weekdayOptions}
        placeholder={t('planning.fields.select_days')}
        {...form.getInputProps('weekdays')}
      />
    );
  }, [form, t, weekdayOptions]);

  const isMobile = useMediaQuery('(max-width: 600px)');
  // moved up to integrate assignableUsers from membersLive

  const handleShare = async () => {
    try {
      // Enforce owner plan cap on members before sharing
      const planningRef = doc(db, 'plannings', id);
      const snap = await getDoc(planningRef);
      if (snap.exists()) {
        const data = snap.data() || {};
        const owner = data.owner;
        if (owner) {
          try {
            const ownerPlan = await PlanService.getCurrentPlan(owner);
            const { maxMembersPerCalendar } = PlanService.getLimits(ownerPlan);
            const membersCount = 1 + ((Array.isArray(data.participants) ? data.participants.length : 0));
            if (membersCount >= maxMembersPerCalendar) {
              toast.show({
                title: 'Plan limit',
                message: 'Your plan does not allow inviting more people to this calendar',
                color: 'red',
              });
              return;
            }
          } catch {}
        }
      }
      const docRef = doc(db, 'plannings', id);
      const code = id; // use doc id as share code
      await updateDoc(docRef, { shareCode: code });
      setShareCode(code);
      setShareOpen(true);
    } catch (e) {
      console.error('Failed to set share code', e);
    }
  };

  const listPeople = async () => {
    setPeopleOpen(true);
  };

  const handleRenamePlanning = async (newName) => {
    try {
      if (!id) return;
      const trimmed = String(newName || '').trim();
      if (!trimmed) return;
      await updateDoc(doc(db, 'plannings', id), { name: trimmed });
      setPlanningName(trimmed);
    } catch (e) {
      console.error('Failed to rename planning', e);
      toast.show({ title: 'Failed to save', message: String(e?.message || e), color: 'red' });
    }
  };

  // Requests are streamed live by useMembersManager; no manual fetch needed here

  return (
    <Container fluid px={0}>
      <PlanningHeader
        name={planningName}
        canEdit={isOwner}
        onRename={handleRenamePlanning}
        onShare={handleShare}
        onShowPeople={listPeople}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenSidebar={() => setSidebarOpen(true)}
        onBack={() => navigate('/app')}
        isMobile={isMobile}
      />

      <div className="planning-content">
        {/* Controls row under header */}
        {isMobile ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8, marginBottom: 8 }}>
            <Button
              fullWidth
              variant="outline"
              rightSection={<Badge color={undeclaredCount ? 'red' : 'gray'} variant={undeclaredCount ? 'filled' : 'light'}>{undeclaredCount}</Badge>}
              onClick={() => setAvailModalOpen(true)}
            >
              {t('planning.declare_availabilities')}
            </Button>
            {canCreate && (
              <Button fullWidth leftSection={<IconPlus size={16} />} onClick={() => setDrawerOpen(true)} style={{ padding: '0 0px' }}>
                {t('planning.add_event')}
              </Button>
            )}
            <PlanningExportMenu
              events={filteredEvents}
              planningName={planningName}
              defaultPlanningName={t('planning.default_name')}
              userLabelById={userLabelById}
              locale={exportLocale}
              t={t}
              fullWidth
            />
          </div>
        ) : (
          <Group justify="flex-end" mb="sm" gap="xs">
            <Button
              variant="outline"
              rightSection={<Badge color={undeclaredCount ? 'red' : 'gray'} variant={undeclaredCount ? 'filled' : 'light'}>{undeclaredCount}</Badge>}
              onClick={() => setAvailModalOpen(true)}
            >
              {t('planning.declare_availabilities')}
            </Button>
            {canCreate && (
              <Button leftSection={<IconPlus size={16} />} onClick={() => setDrawerOpen(true)}>
                {t('planning.add_event')}
              </Button>
            )}
            <PlanningExportMenu
              events={filteredEvents}
              planningName={planningName}
              defaultPlanningName={t('planning.default_name')}
              userLabelById={userLabelById}
              locale={exportLocale}
              t={t}
            />
          </Group>
        )}

        {/* Main content: 1/3 - 2/3 split on desktop, full calendar + drawer on mobile */}
        {isMobile ? (
          <>
            <Group gap={8} mb={8} wrap="wrap">
              <Select
                placeholder="Filtrer par membre"
                data={filterUserOptions}
                value={filterUserId}
                onChange={(v) => setFilterUserId(v || null)}
                clearable
                searchable
                style={{ minWidth: 220 }}
              />
              <TextInput
                placeholder="Rechercher un évènement"
                value={filterText}
                onChange={(e) => setFilterText(e.currentTarget.value)}
                style={{ minWidth: 220 }}
              />
            </Group>
            <MobileDayNavigator
              events={filteredEvents}
              assignableUsers={assignableUsers}
              onEventClick={handleEventClick}
            />
          </>
        ) : (
          <div className="planning-layout">
            <div className="sidebar">
              <PlanningSidebar
                planningId={id}
                ownerId={ownerId}
                isOwner={isOwner}
                assignableUsers={assignableUsers}
                user={user}
                events={filteredEvents}
                optimizationProfile={optimizationProfile}
                optimizationOverrides={optimizationOverrides}
                onLocalAssignmentsChange={(eventIds, assigned) => {
                  if (Array.isArray(eventIds)) {
                    eventIds.forEach((eid) => updateEventAssignmentsLocal(eid, assigned));
                  }
                }}
              />
            </div>
            <div className="calendar calendar-container fc-qt">
              <Group gap={8} mb={8} wrap="wrap">
                <Select
                  placeholder={t('planning.filter_by_member')}
                  data={filterUserOptions}
                  value={filterUserId}
                  onChange={(v) => setFilterUserId(v || null)}
                  clearable
                  searchable
                  style={{ minWidth: 240 }}
                />
                <TextInput
                  placeholder={t('planning.search_event')}
                  value={filterText}
                  onChange={(e) => setFilterText(e.currentTarget.value)}
                  style={{ minWidth: 260 }}
                />
              </Group>
              <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          initialDate={new Date()}
          customButtons={{
            clearAllocations: {
              text: '🧹 ' + t('planning.clear'),
              click: async () => {
                if (clearing) return;
                const cal = calendarRef.current?.getApi?.();
                if (!cal) return;
                const start = cal.view?.currentStart;
                const end = cal.view?.currentEnd;
                if (!start || !end) return;
                const startMs = new Date(start).getTime();
                const endMs = new Date(end).getTime();
                const inScope = (events || []).filter((ev) => {
                  const s = new Date(ev.start).getTime();
                  const e = new Date(ev.end || ev.start).getTime();
                  return Number.isFinite(s) && Number.isFinite(e) && s < endMs && e > startMs;
                });
                const ids = inScope.map((ev) => ev.id);
                if (!ids.length) return;
                if (!(await confirm({ title: t('planning.clear_allocations_title'), message: t('planning.clear_allocations_message', { count: ids.length }), color: 'red' }))) return;
                setClearing(true);
                try {
                  await OptimizationService.clearAssignmentsForEvents(id, ids);
                  // Optimistic local UI update
                  ids.forEach((eid) => updateEventAssignmentsLocal(eid, []));
                } finally {
                  setClearing(false);
                }
              },
            },
          }}
          headerToolbar={{
            left: 'prev,next title today clearAllocations',
            center: '',
            right: 'timeGridDay,timeGridWeek,dayGridMonth'
          }}
          buttonText={{ today: t('planning.cal.today'), month: t('planning.cal.month'), week: t('planning.cal.week'), day: t('planning.cal.day') }}
          dayHeaderFormat={{ weekday: 'short', day: 'numeric', month: 'numeric' }}
          locales={[frLocale]}
          locale={isFr ? 'fr' : 'en'}
          // Allow scrolling across full day, but default to morning
          scrollTime={'06:00:00'}
          slotMinTime={'00:00:00'}
          slotMaxTime={'24:00:00'}
          contentHeight={650}
          events={filteredEvents}
          eventClassNames={(arg) => {
            const assignments = Array.isArray(arg.event.extendedProps?.assignments) ? arg.event.extendedProps.assignments : [];
            return assignments.length > 0 ? ['evt-assigned'] : ['evt-unassigned'];
          }}
          eventContent={(arg) => {
            const title = arg.event.title || t('planning.default_event_title');
            const assignments = Array.isArray(arg.event.extendedProps?.assignments) ? arg.event.extendedProps.assignments : [];
            const names = assignments.map((a) => userLabelById.get(a.uid) || a.uid).join(', ');
            const hasLinks = Array.isArray(arg.event.extendedProps?.linkedEventIds) && arg.event.extendedProps.linkedEventIds.length > 0;
            return (
              <div className={`evt-content${hasLinks ? ' has-link' : ''}`}>
                {hasLinks && <span className="evt-linked-left" title={t('planning.details.linked')}>🔗</span>}
                <div className="evt-title">
                  <span className="evt-title-text">{title}</span>
                </div>
                {names && <div className="evt-assignees">{names}</div>}
              </div>
            );
          }}
          // Use fixed content height so the grid scrolls
          nowIndicator={true}
          selectable={false}
          editable={false}
          eventClick={handleEventClick}
        />
            </div>
          </div>
        )}
      </div>

      <Drawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        position="right"
        title={t('planning.add_event_title')}
        size="md"
      >
        <form
          onSubmit={form.onSubmit((vals) => handleCreate(vals))}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          <Stack gap="sm">
            <TextInput label={t('planning.form.name_label')} placeholder={t('planning.form.name_placeholder')} required {...form.getInputProps('title')} />
            <TextInput label={t('planning.form.location_label')} placeholder={t('planning.form.location_placeholder')} {...form.getInputProps('location')} />
            <Textarea label={t('planning.form.description_label')} placeholder={t('planning.form.description_placeholder')} autosize minRows={2} {...form.getInputProps('description')} />
            <TextInput
              label={t('planning.form.start_label')}
              type="datetime-local"
              step={1800}
              required
              {...form.getInputProps('startAt')}
            />
            <Select
              label={t('planning.form.duration_label')}
              description={t('planning.form.duration_hint')}
              data={useMemo(() => buildDurationOptions(), [])}
              searchable
              creatable
              getCreateLabel={(query) => t('planning.form.use_value', { value: query })}
              onCreate={(query) => {
                const mins = roundToNearest30(hhmmToMinutes(query));
                if (!Number.isFinite(mins) || mins <= 0) return null;
                const item = { value: String(mins), label: minutesToHHMM(mins) };
                return item;
              }}
              value={String(form.values.durationMinutes || 60)}
              onChange={(val) => {
                const mins = Number(val);
                if (Number.isFinite(mins)) form.setFieldValue('durationMinutes', mins);
              }}
              placeholder={t('planning.form.duration_placeholder')}
              required
              withinPortal
            />
            <Select
              label={t('planning.form.effective_time')}
              description={t('planning.form.effective_hint')}
              data={(() => { const dur = Number(form.values.durationMinutes)||0; const step=15; const opts=[]; for(let m=0;m<=dur;m+=step){ opts.push({ value:String(m), label: minutesToHHMM(m) }); } return opts; })()}
              value={String(Number(form.values.effectiveMinutes) || 0)}
              onChange={(val) => {
                const dur = Number(form.values.durationMinutes) || 0;
                const eff = Math.max(0, Math.min(dur, Number(val) || 0));
                form.setFieldValue('effectiveMinutes', eff);
              }}
              searchable
              withinPortal
            />
            <Stack gap="xs">
              <Text fw={600}>{t('planning.form.roles_required')}</Text>
              {(form.values.rolesRequired || []).map((row, idx) => (
                <Group key={idx} gap="xs" align="flex-end">
                  <Autocomplete
                    label={t('planning.form.role')}
                    placeholder={t('planning.form.role_placeholder')}
                    style={{ flex: 1 }}
                    data={roleSuggestions}
                    value={row.role}
                    onChange={(val) => {
                      const rows = [...form.values.rolesRequired];
                      rows[idx] = { ...rows[idx], role: val };
                      form.setFieldValue('rolesRequired', rows);
                    }}
                  />
                  <NumberInput
                    label={t('planning.form.count')}
                    min={1}
                    step={1}
                    value={Number(row.count) || 1}
                    onChange={(v) => {
                      const rows = [...form.values.rolesRequired];
                      rows[idx] = { ...rows[idx], count: Number(v) || 1 };
                      form.setFieldValue('rolesRequired', rows);
                    }}
                    style={{ width: 120 }}
                  />
                  <ActionIcon aria-label={t('planning.form.remove_role')} color="red" variant="light" onClick={() => {
                    const rows = [...(form.values.rolesRequired || [])];
                    rows.splice(idx, 1);
                    form.setFieldValue('rolesRequired', rows);
                  }}>
                    <IconX size={16} />
                  </ActionIcon>
                </Group>
              ))}
              <Group>
                <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => {
                  form.setFieldValue('rolesRequired', [...(form.values.rolesRequired || []), { role: '', count: 1 }]);
                }}>{t('planning.form.add_role')}</Button>
              </Group>
            </Stack>
            <Select label={t('planning.form.frequency')} data={freqOptions} {...form.getInputProps('frequency')} />
            {(form.values.frequency && form.values.frequency !== 'none' && form.values.frequency !== 'daily' && !form.errors.weekdays) && (
              weekdaysControl
            )}
            {form.values.frequency && form.values.frequency !== 'none' && (
              <TextInput label={t('planning.form.repeat_until')} type="date" {...form.getInputProps('untilDate')} />
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={() => setDrawerOpen(false)} disabled={saving}>{t('common.cancel')}</Button>
              <Button type="submit" loading={saving}>{t('common.create')}</Button>
            </Group>
          </Stack>
        </form>
      </Drawer>

      {/* Event details drawer */}
      <Drawer
        opened={eventDrawerOpen}
        onClose={() => setEventDrawerOpen(false)}
        position="right"
        title={t('planning.details.title')}
        size="md"
      >
        {!selectedEvent ? (
          <Text>{t('common.loading')}</Text>
        ) : (
          <Stack gap="sm">
            {/* View mode by default; editing requires clicking Edit */}
            {!isEditingEvent || !isOwner ? (
              <>
                <Group justify="space-between" align="center">
                  <Title order={4}>{selectedEvent.title || t('planning.default_event_title')}</Title>
                  {isOwner && (
                    <Group>
                      <Button
                        variant="default"
                        color="red"
                        leftSection={<IconTrash size={16} />}
                        onClick={() => setDeleteModalOpen(true)}
                      >
                        {t('common.delete')}
                      </Button>
                      <Button leftSection={<IconPencil size={16} />} onClick={() => setIsEditingEvent(true)}>
                        {t('common.edit')}
                      </Button>
                    </Group>
                  )}
                </Group>
                {selectedEvent.description && (
                  <Text>{selectedEvent.description}</Text>
                )}
                {selectedEvent.location && (
                  <Text><b>{t('planning.details.location')}:</b> {selectedEvent.location}</Text>
                )}
                <Text><b>{t('planning.details.start')}:</b> {formatDisplay(selectedEvent.start)}</Text>
                <Text><b>{t('planning.details.end')}:</b> {formatDisplay(selectedEvent.end)}</Text>
                {Number(selectedEvent?.effectiveMinutes) > 0 && (
                  <Text><b>{t('planning.details.effective')}:</b> {(() => { const m=Math.round(Number(selectedEvent.effectiveMinutes)||0); const h=Math.floor(m/60).toString().padStart(2,'0'); const mm=(m%60).toString().padStart(2,'0'); return `${h}:${mm}`; })()}</Text>
                )}
                {/* Compact: remove separate Roles required table; info is included in Assignments below */}
                <Stack gap="sm">
                  <Text fw={600}>{t('planning.assignments')}</Text>
                  {(Array.isArray(selectedEvent.rolesRequired) && selectedEvent.rolesRequired.length) ? (
                    <Table striped highlightOnHover stickyHeader styles={{ th: { padding: '4px 8px' }, td: { padding: '6px 8px', verticalAlign: 'middle' } }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th style={{ width: '45%' }}>{t('planning.table.role')}</Table.Th>
                          <Table.Th style={{ width: 80 }}>{t('planning.table.staff_required')}</Table.Th>
                          <Table.Th style={{ width: '55%' }}>{t('planning.table.assigned')}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {selectedEvent.rolesRequired.map((r, i) => {
                          const required = Math.max(1, Number(r.count) || 1);
                          const assignedForRole = (selectedEvent.assignments || []).filter((a) => a.role === r.role);
                          const names = assignedForRole.map((a) => userLabelById.get(a.uid) || a.uid);
                          return (
                            <Table.Tr key={i}>
                              <Table.Td>
                                <Tooltip label={r.role} withArrow>
                                  <Badge size="sm" variant="light">{r.role}</Badge>
                                </Tooltip>
                              </Table.Td>
                              <Table.Td><Badge color="gray" variant="outline">{required}</Badge></Table.Td>
                          <Table.Td>
                            {names.length ? (
                              <Stack gap={4} align="flex-start">
                                {names.slice(0, 4).map((n, idx) => (
                                  <Tooltip key={idx} label={n} withArrow>
                                    <Badge size="xs" variant="outline">{n}</Badge>
                                  </Tooltip>
                                ))}
                                {names.length > 4 && (
                                  <Badge size="xs" variant="outline">+{names.length - 4} {t('planning.more')}</Badge>
                                )}
                              </Stack>
                            ) : (
                              <Text c="dimmed">{t('planning.none')}</Text>
                            )}
                          </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  ) : (
                    <Text c="dimmed">{t('planning.none')}</Text>
                  )}
                </Stack>
                <Stack gap="sm" mt="sm">
                  <Text fw={600}>{t('planning.linked_events')}</Text>
                  {Array.isArray(selectedEvent.linkedEventIds) && selectedEvent.linkedEventIds.length ? (
                    <Group gap="xs" wrap>
                      {selectedEvent.linkedEventIds.map((lid, idx) => {
                        const ev = (events || []).find((e) => e.id === lid);
                        const name = ev?.title || lid;
                        return (
                          <Tooltip key={lid + '_' + idx} label={name} withArrow>
                            <Badge size="sm" radius="xl" variant="light">{name}</Badge>
                          </Tooltip>
                        );
                      })}
                    </Group>
                  ) : (
                    <Text c="dimmed">{t('planning.none')}</Text>
                  )}
                </Stack>
                {/* Diagnostics: Why not assigned */}
                <Stack gap="xs" mt="sm">
                  <Text fw={600}>{t('planning.why_not_assigned')}</Text>
                  {(() => {
                    const ev = selectedEvent;
                    if (!ev) return null;
                    const membersArr = Array.isArray(membersLive) ? membersLive : [];
                    const rows = diagnoseWhyNotAssignedForEvent(ev, {
                      members: membersArr,
                      events: events || [],
                      optimizationOverrides: optimizationOverrides || {},
                    });
                    // If no rows, provide a clear explanation
                    if (!rows.length) {
                      const reqRoles = Array.isArray(ev.rolesRequired) ? ev.rolesRequired.map(r => String(r.role||'').trim()).filter(Boolean) : [];
                      if (reqRoles.length && membersArr.length) {
                        return (
                          <Text c="red">
                            {t('planning.diagnostics.no_participants_match')}: {reqRoles.join(', ')}
                          </Text>
                        );
                      }
                      if (!membersArr.length) {
                        return (
                          <Text c="red">{t('planning.diagnostics.no_participants')}</Text>
                        );
                      }
                      return (
                        <Text c="red">{t('planning.diagnostics.no_one_available')}</Text>
                      );
                    }
                    return (
                      <Stack gap={4}>
                        {rows.map((r) => {
                          const reasonText = (
                            r.reason === 'Available' ? t('availability.available') :
                            r.reason === 'Overlap with another assigned event' ? t('planning.diagnostics.overlap') :
                            r.reason === 'Daily rest not met' ? t('planning.diagnostics.daily_rest') :
                            r.reason === 'Daily cap exceeded' ? t('planning.diagnostics.daily_cap') :
                            r.reason === 'Weekly cap exceeded' ? t('planning.diagnostics.weekly_cap') :
                            r.reason === 'Daily amplitude cap exceeded' ? t('planning.diagnostics.daily_amplitude') :
                            r.reason === 'Min break between locations not met' ? t('planning.diagnostics.location_min_break') :
                            r.reason === 'Max continuous hours exceeded' ? t('planning.diagnostics.continuous_work') :
                            r.reason === 'Max consecutive days exceeded' ? t('planning.diagnostics.consecutive_days') :
                            r.reason === 'Weekly days off constraint' ? t('planning.diagnostics.weekly_days_off') :
                            r.reason === 'Does not match required roles' ? t('planning.diagnostics.roles_mismatch') :
                            r.reason
                          );
                          const isOk = r.reason === 'Available';
                          return (
                            <Group key={r.uid} gap={8}>
                              <Badge variant={isOk ? 'light' : 'outline'} color={isOk ? 'green' : 'red'}>{r.label}</Badge>
                              <Text size="sm" c={isOk ? 'green' : 'red'}>{reasonText}</Text>
                            </Group>
                          );
                        })}
                      </Stack>
                    );
                  })()}
                </Stack>
                {isOwner && selectedEvent && (
                  <AdminEventAvailabilities planningId={id} event={selectedEvent} members={assignableUsers} />
                )}
                {/* Removed owner unassign buttons; edit assignments directly in table above */}
                {!isOwner && (
                  <Group justify="space-between" align="center" mt="md">
                    <div />
                    <Group>
              <Button
                onClick={assignToMe}
                loading={saving}
                disabled={!user || ((selectedEvent.assignments || []).some((a) => a.uid === user?.uid))}
              >
                {t('planning.details.assign_to_me')}
              </Button>
              <Button
                variant="default"
                onClick={unassignMe}
                disabled={!user || !((selectedEvent.assignments || []).some((a) => a.uid === user?.uid))}
              >
                {t('planning.details.unassign_me')}
              </Button>
              <Button variant="default" onClick={() => setEventDrawerOpen(false)}>{t('common.close')}</Button>
              </Group>
            </Group>
          )}
              </>
            ) : (
              <>
                {selectedEvent.seriesId && (
                  <>
                    <SegmentedControl
                      fullWidth
                      value={editScope}
                      onChange={setEditScope}
                      data={[
                        { label: t('planning.series.this_event'), value: 'single' },
                        { label: t('planning.series.entire_series'), value: 'series' },
                      ]}
                    />
                    {editScope === 'series' && (
                      <Stack gap={6}>
                        <Text size="sm" c="dimmed">{t('planning.series.edit_series_info')}</Text>
                        <Checkbox
                          label={t('planning.series.change_recurrence')}
                          checked={changeRecurrence}
                          onChange={(e) => setChangeRecurrence(e.currentTarget.checked)}
                        />
                        {changeRecurrence && (
                          <>
                            <Select
                              label={t('planning.series.new_frequency')}
                              data={freqOptions}
                              placeholder={t('planning.series.select_frequency')}
                              value={seriesEditFrequency || null}
                              onChange={(v) => setSeriesEditFrequency(v || '')}
                            />
                            {(seriesEditFrequency && seriesEditFrequency !== 'none' && seriesEditFrequency !== 'daily') && (
                              <MultiSelect
                                label={t('planning.fields.days_of_week')}
                                data={weekdayOptions}
                                placeholder={t('planning.fields.select_days')}
                                value={seriesEditWeekdays}
                                onChange={setSeriesEditWeekdays}
                              />
                            )}
                            {seriesEditFrequency && seriesEditFrequency !== 'none' && (
                              <TextInput
                                label={t('planning.form.repeat_until')}
                                type="date"
                                value={seriesEditUntilDate}
                                onChange={(e) => setSeriesEditUntilDate(e.currentTarget.value)}
                              />
                            )}
                          </>
                        )}
                      </Stack>
                    )}
                  </>
                )}
                <TextInput
                  label={t('planning.form.name_label')}
                  value={selectedEvent.title}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, title: e.currentTarget.value })}
                />
                <TextInput
                  label={t('planning.form.location_label')}
                  placeholder={t('planning.form.location_placeholder')}
                  value={selectedEvent.location || ''}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, location: e.currentTarget.value })}
                />
                <Textarea
                  label={t('planning.form.description_label')}
                  value={selectedEvent.description}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, description: e.currentTarget.value })}
                />
                <TextInput
                  label={t('planning.details.start')}
                  type="datetime-local"
                  value={formatDateTimeLocal(selectedEvent.start)}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, start: new Date(e.currentTarget.value) })}
                />
                <TextInput
                  label={t('planning.details.end')}
                  type="datetime-local"
                  value={formatDateTimeLocal(selectedEvent.end)}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, end: new Date(e.currentTarget.value) })}
                />
                <Select
                  label={t('planning.form.effective_time')}
                  data={(() => { const durationMin = Math.max(0, Math.round((new Date(selectedEvent.end).getTime() - new Date(selectedEvent.start).getTime())/60000)); const step=15; const opts=[]; for(let m=0;m<=durationMin;m+=step){ opts.push({ value:String(m), label: minutesToHHMM(m)});} return opts; })()}
                  value={String(Number(selectedEvent.effectiveMinutes) || 0)}
                  onChange={(val) => {
                    const durationMin = Math.max(0, Math.round((new Date(selectedEvent.end).getTime() - new Date(selectedEvent.start).getTime())/60000));
                    const eff = Math.max(0, Math.min(durationMin, Number(val) || 0));
                    setSelectedEvent({ ...selectedEvent, effectiveMinutes: eff });
                  }}
                  searchable
                  withinPortal
                />
                <MultiSelect
                  label={t('planning.details.linked_same_day')}
                  placeholder={t('planning.details.select_events')}
                  data={(events || [])
                    .filter((ev) => {
                      try {
                        const a = new Date(selectedEvent.start);
                        const b = new Date(ev.start);
                        return ev.id !== selectedEvent.id && a.toDateString() === b.toDateString();
                      } catch { return false; }
                    })
                    .map((ev) => ({ value: ev.id, label: ev.title || ev.id }))}
                  value={Array.isArray(selectedEvent.linkedEventIds) ? selectedEvent.linkedEventIds : []}
                  onChange={(vals) => setSelectedEvent({ ...selectedEvent, linkedEventIds: Array.isArray(vals) ? vals.filter(Boolean) : [] })}
                  searchable
                  clearable
                />
                <Stack gap="xs">
                  <Text fw={600}>{t('planning.details.roles_and_assignments')}</Text>
                  <Table
                    striped
                    stickyHeader
                    style={{ tableLayout: 'fixed', width: '100%' }}
                    styles={{ th: { padding: '4px 8px' }, td: { padding: '6px 8px', verticalAlign: 'middle' } }}
                  >
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: '50%' }}>Role</Table.Th>
                        <Table.Th style={{ width: 90, textAlign: 'center' }}>Req.</Table.Th>
                        <Table.Th>Assigned</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {(selectedEvent.rolesRequired || []).map((row, idx) => {
                        const limit = Math.max(1, Number(row.count) || 1);
                        const current = (selectedEvent.assignments || []).filter((a) => a.role === row.role).map((a) => a.uid);
                        const extra = Math.max(0, current.length - 3);
                        return (
                          <Table.Tr key={idx}>
                            <Table.Td>
                              <Group gap={6} wrap="nowrap">
                                <Tooltip label={row.role} withArrow>
                                  <Badge size="sm" variant="light">{row.role}</Badge>
                                </Tooltip>
                                <Popover withArrow position="bottom-start">
                                  <Popover.Target>
                                    <ActionIcon size="xs" variant="subtle" aria-label={t('planning.details.rename_role')}>
                                      <IconPencil size={12} />
                                    </ActionIcon>
                                  </Popover.Target>
                                  <Popover.Dropdown>
                                      <Autocomplete
                                      placeholder={t('planning.details.role_name_placeholder')}
                                      data={roleSuggestions}
                                      value={row.role}
                                      onChange={(val) => {
                                        const rows = [...(selectedEvent.rolesRequired || [])];
                                        const oldRole = rows[idx].role;
                                        rows[idx] = { ...rows[idx], role: val };
                                        const updatedAssign = (selectedEvent.assignments || []).map((a) => a.role === oldRole ? { ...a, role: val } : a);
                                        setSelectedEvent({ ...selectedEvent, rolesRequired: rows, assignments: updatedAssign });
                                      }}
                                      comboboxProps={{ withinPortal: true }}
                                    />
                                    <Group justify="space-between" mt={6}>
                                      <div />
                                      <Button size="xs" color="red" variant="light" onClick={() => {
                                        const rows = [...(selectedEvent.rolesRequired || [])];
                                        const removed = rows.splice(idx, 1)[0];
                                        const filteredAssign = (selectedEvent.assignments || []).filter((a) => a.role !== removed.role);
                                        setSelectedEvent({ ...selectedEvent, rolesRequired: rows, assignments: filteredAssign });
                                      }}>Remove role</Button>
                                    </Group>
                                  </Popover.Dropdown>
                                </Popover>
                              </Group>
                            </Table.Td>
                            <Table.Td style={{ textAlign: 'center' }}>
                              <Group gap={4} justify="center">
                                <ActionIcon size="xs" variant="subtle" onClick={() => {
                                  const rows = [...(selectedEvent.rolesRequired || [])];
                                  const next = Math.max(1, (Number(rows[idx].count) || 1) - 1);
                                  rows[idx] = { ...rows[idx], count: next };
                                  setSelectedEvent({ ...selectedEvent, rolesRequired: rows });
                                }}>-</ActionIcon>
                                <Text fw={600} size="sm" style={{ minWidth: 16, textAlign: 'center' }}>{Number(row.count) || 1}</Text>
                                <ActionIcon size="xs" variant="subtle" onClick={() => {
                                  const rows = [...(selectedEvent.rolesRequired || [])];
                                  const next = Math.max(1, (Number(rows[idx].count) || 1) + 1);
                                  rows[idx] = { ...rows[idx], count: next };
                                  setSelectedEvent({ ...selectedEvent, rolesRequired: rows });
                                }}>+</ActionIcon>
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Popover withArrow position="bottom-start">
                                <Popover.Target>
                                  <Stack gap={4} align="flex-start" style={{ cursor: 'pointer' }}>
                                    {current.slice(0, 3).map((uid) => {
                                      const label = userLabelById.get(uid) || uid;
                                      return (
                                        <Tooltip key={uid} label={label} withArrow>
                                          <Badge size="xs" variant="outline">{label}</Badge>
                                        </Tooltip>
                                      );
                                    })}
                                    {extra > 0 && <Badge size="xs" variant="outline">+{extra}</Badge>}
                                    {current.length === 0 && <Text c="dimmed" size="sm">{t('planning.details.assign_placeholder')}</Text>}
                                  </Stack>
                                </Popover.Target>
                                <Popover.Dropdown>
                              <MultiSelect
                                data={assignableUsers}
                                value={current}
                                onChange={(vals) => {
                                  const limited = Array.isArray(vals) ? vals.slice(0, limit) : [];
                                  const existing = Array.isArray(selectedEvent.assignments) ? selectedEvent.assignments : [];
                                  // Remove any assignment for this role or for users newly selected (enforce unique uid across roles)
                                  const pruned = existing.filter((a) => a.role !== row.role && !limited.includes(a.uid));
                                  const next = [...pruned, ...limited.map((uid) => ({ role: row.role, uid }))];
                                  // Deduplicate by uid just in case
                                  const seen = new Set();
                                  const unique = [];
                                  for (const a of next) {
                                    if (!seen.has(a.uid)) { seen.add(a.uid); unique.push(a); }
                                  }
                                  setSelectedEvent({ ...selectedEvent, assignments: unique });
                                }}
                                placeholder={assignableUsers.length ? t('planning.details.choose_users') : t('planning.details.no_users')}
                                searchable
                                clearable
                                comboboxProps={{ withinPortal: true }}
                                size="xs"
                              />
                                </Popover.Dropdown>
                              </Popover>
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                  <Group>
                    <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={() => {
                      setSelectedEvent({ ...selectedEvent, rolesRequired: [...(selectedEvent.rolesRequired || []), { role: '', count: 1 }] });
                    }}>{t('planning.form.add_role')}</Button>
                  </Group>
                </Stack>
                <Group justify="space-between" mt="md">
                  <Button
                    variant="default"
                    color="red"
                    leftSection={<IconTrash size={16} />}
                    onClick={() => setDeleteModalOpen(true)}
                  >
                    {t('common.delete')}
                  </Button>
                  <Group>
                    <Button variant="default" onClick={() => setIsEditingEvent(false)} disabled={saving}>{t('common.cancel')}</Button>
                    <Button onClick={saveSelectedEvent} loading={saving}>{t('common.save')}</Button>
                  </Group>
                </Group>
              </>
            )}
          </Stack>
        )}
      </Drawer>

      {/* Delete modal for single vs series */}
      <Modal opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title={t('planning.delete_event_title')}>
        {selectedEvent?.seriesId ? (
          <Stack>
            <Text>{t('planning.delete_event_series_question')}</Text>
            <Group>
              <Button color="red" variant="outline" onClick={deleteSingle}>{t('planning.series.this_event')}</Button>
              <Button color="red" onClick={deleteSeries}>{t('planning.series.entire_series')}</Button>
            </Group>
          </Stack>
        ) : (
          <Stack>
            <Text>{t('planning.series.not_in_series')}</Text>
            <Group>
              <Button color="red" onClick={deleteSingle}>{t('planning.series.delete_event')}</Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Availabilities modal */}
      <AvailabilityModal
        planningId={id}
        user={user}
        events={events}
        opened={availModalOpen}
        onClose={() => setAvailModalOpen(false)}
      />

      {/* Mobile sidebar drawer */}
      <Drawer
        opened={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        position="left"
        title={t('planning.overview_title')}
        size="md"
      >
        <PlanningSidebar
          planningId={id}
          ownerId={ownerId}
          assignableUsers={assignableUsers}
          user={user}
          events={events}
          optimizationProfile={optimizationProfile}
          optimizationOverrides={optimizationOverrides}
          onLocalAssignmentsChange={(eventIds, assigned) => {
            if (Array.isArray(eventIds)) {
              eventIds.forEach((eid) => updateEventAssignmentsLocal(eid, assigned));
            }
          }}
        />
      </Drawer>

      {/* Settings modal */}
      <PlanningSettingsModal
        opened={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        profile={optimizationProfile}
        weeklyDefaultInternal={optimizationOverrides.weeklyDefaultInternal}
        weeklyDefaultExternal={optimizationOverrides.weeklyDefaultExternal}
        mediaMaxContinuousHours={optimizationOverrides.mediaMaxContinuousHours}
        mediaMinBreakMinutes={optimizationOverrides.mediaMinBreakMinutes}
        mediaDailyMaxHours={optimizationOverrides.mediaDailyMaxHours}
        mediaMinDailyRestHours={optimizationOverrides.mediaMinDailyRestHours}
        mediaAmplitudeMaxHours={optimizationOverrides.mediaAmplitudeMaxHours}
        mediaMaxConsecutiveDays={optimizationOverrides.mediaMaxConsecutiveDays}
        mediaMinWeeklyDaysOff={optimizationOverrides.mediaMinWeeklyDaysOff}
        locationMinBreakMinutes={optimizationOverrides.locationMinBreakMinutes}
        mediaSeriesConsistencyEnabled={optimizationOverrides.mediaSeriesConsistencyEnabled}
        mediaSeriesConsistencyPenalty={optimizationOverrides.mediaSeriesConsistencyPenalty}
        onChangeProfile={setOptimizationProfile}
        onChangeWeeklyDefaultInternal={(v) => setOptimizationOverrides((prev) => ({ ...prev, weeklyDefaultInternal: Number(v) || 40 }))}
        onChangeWeeklyDefaultExternal={(v) => setOptimizationOverrides((prev) => ({ ...prev, weeklyDefaultExternal: Number(v) || 44 }))}
        onChangeMediaMaxContinuousHours={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaMaxContinuousHours: v }))}
        onChangeMediaMinBreakMinutes={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaMinBreakMinutes: v }))}
        onChangeMediaDailyMaxHours={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaDailyMaxHours: v }))}
        onChangeMediaMinDailyRestHours={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaMinDailyRestHours: v }))}
        onChangeMediaAmplitudeMaxHours={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaAmplitudeMaxHours: v }))}
        onChangeMediaMaxConsecutiveDays={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaMaxConsecutiveDays: v }))}
        onChangeMediaMinWeeklyDaysOff={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaMinWeeklyDaysOff: v }))}
        onChangeLocationMinBreakMinutes={(v) => setOptimizationOverrides((prev) => ({ ...prev, locationMinBreakMinutes: v }))}
        onChangeMediaSeriesConsistencyEnabled={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaSeriesConsistencyEnabled: !!v }))}
        onChangeMediaSeriesConsistencyPenalty={(v) => setOptimizationOverrides((prev) => ({ ...prev, mediaSeriesConsistencyPenalty: Number(v) || 0 }))}
        onSaveProfile={async (value, overrides) => {
          try {
            if (!id) return;
            const payload = { optimizationProfile: value || 'default' };
            if (overrides && typeof overrides.mediaMaxContinuousHours === 'number') {
              payload.optimizationMediaMaxContinuousHours = overrides.mediaMaxContinuousHours;
            }
            if (overrides && typeof overrides.mediaMinBreakMinutes === 'number') {
              payload.optimizationMediaMinBreakMinutes = overrides.mediaMinBreakMinutes;
            }
            if (overrides && typeof overrides.mediaDailyMaxHours === 'number') {
              payload.optimizationMediaDailyMaxHours = overrides.mediaDailyMaxHours;
            }
            if (overrides && typeof overrides.mediaMinDailyRestHours === 'number') {
              payload.optimizationMediaMinDailyRestHours = overrides.mediaMinDailyRestHours;
            }
            if (overrides && typeof overrides.mediaAmplitudeMaxHours === 'number') {
              payload.optimizationMediaAmplitudeMaxHours = overrides.mediaAmplitudeMaxHours;
            }
            if (overrides && typeof overrides.mediaMaxConsecutiveDays === 'number') {
              payload.optimizationMediaMaxConsecutiveDays = overrides.mediaMaxConsecutiveDays;
            }
            if (overrides && typeof overrides.mediaMinWeeklyDaysOff === 'number') {
              payload.optimizationMediaMinWeeklyDaysOff = overrides.mediaMinWeeklyDaysOff;
            }
            if (overrides && typeof overrides.locationMinBreakMinutes === 'number') {
              payload.optimizationLocationMinBreakMinutes = overrides.locationMinBreakMinutes;
            }
            if (overrides && typeof overrides.mediaSeriesConsistencyEnabled === 'boolean') {
              payload.optimizationMediaSeriesConsistency = overrides.mediaSeriesConsistencyEnabled;
            }
            if (overrides && typeof overrides.mediaSeriesConsistencyPenalty === 'number') {
              payload.optimizationMediaSeriesConsistencyWeight = overrides.mediaSeriesConsistencyPenalty;
            }
            if (overrides && typeof overrides.weeklyDefaultInternal === 'number') {
              payload.optimizationWeeklyDefaultInternal = overrides.weeklyDefaultInternal;
            }
            if (overrides && typeof overrides.weeklyDefaultExternal === 'number') {
              payload.optimizationWeeklyDefaultExternal = overrides.weeklyDefaultExternal;
            }
            await updateDoc(doc(db, 'plannings', id), payload);
      toast.show({ title: 'Saved', message: 'Optimization profile updated', color: 'green' });
          } catch (e) {
            toast.show({ title: 'Failed to save', message: String(e?.message || e), color: 'red' });
          }
        }}
      />

      {/* Manage members modal */}
      <ManageMembersModal
        opened={peopleOpen}
        onClose={() => setPeopleOpen(false)}
        isOwner={isOwner}
        owners={ownersHook}
        participants={membersLive}
        atMemberCap={atMemberCap}
        requests={requestsLive}
        onApprove={approveRequest}
        onReject={rejectRequest}
        onRemove={removeParticipant}
        roleSuggestions={roleSuggestions}
        onAddRole={addRole}
        onRemoveRole={removeRole}
        onAddGhost={addGhostMember}
        onSetContract={setContract}
        onSetWeeklyHours={setWeeklyHoursMax}
        onMakeOwner={makeOwner}
        onRemoveOwner={removeOwner}
      />

      {/* Share modal (shared) */}
      <ShareCalendarModal opened={shareOpen} onClose={() => setShareOpen(false)} shareCode={shareCode} />
    </Container>
  );
}
